import { Link } from 'react-router';
import { getHighestRiskLevel } from '../constants/project';
import { FaMapMarkerAlt, FaTasks, FaUsers } from 'react-icons/fa';

// Extract city from site - prioritize Google Address data
const getCityFromSite = (site: any): string => {
  if (!site) return '';

  if (site.google_address_data) {
    const { city, state, locality, administrative_area_level_1 } = site.google_address_data;
    if (locality && administrative_area_level_1) return `${locality}, ${administrative_area_level_1}`;
    if (locality) return locality;
    if (city && state) return `${city}, ${state}`;
    if (city) return city;
  }

  const address = site.address;
  if (!address) return '';

  const parts = address.split(',').map((part: string) => part.trim());
  if (parts.length >= 2) {
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      if (!/^\d+$/.test(part) && !/^[A-Z]{2}$/.test(part) && part.length > 2) {
        const cityPart = part.split(' ')[0];
        if (cityPart && cityPart.length > 2) return cityPart;
        return part;
      }
    }
    return parts[1];
  }
  return parts[0];
};

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  'NOT_STARTED': { label: 'Not Started', bg: 'bg-[#f5f5f5]', text: 'text-[#888]', dot: 'bg-[#ccc]' },
  'IN_PROGRESS': { label: 'In Progress', bg: 'bg-[#f0fdf4]', text: 'text-[#16a34a]', dot: 'bg-[#16a34a]' },
  'COMPLETED':   { label: 'Completed',   bg: 'bg-[#f0fdf4]', text: 'text-[#15803d]', dot: 'bg-[#15803d]' },
  'ON_HOLD':     { label: 'On Hold',     bg: 'bg-[#fefce8]', text: 'text-[#a16207]', dot: 'bg-[#a16207]' },
};

const RISK_COLOR: Record<string, string> = {
  'NONE': '#aaa',
  'LOW': '#22C55E',
  'MEDIUM': '#EAB308',
  'HIGH': '#F97316',
  'CRITICAL': '#EF4444',
};

const RISK_STRIPE: Record<string, string> = {
  'NONE': 'bg-[#ddd]',
  'LOW': 'bg-green-500',
  'MEDIUM': 'bg-yellow-500',
  'HIGH': 'bg-orange-500',
  'CRITICAL': 'bg-red-500',
};

const getProjectHighestRisk = (project: any): string => {
  const risks: string[] = [];
  (project.items || []).forEach((item: any) => { if (item.risks) risks.push(item.risks); });
  (project.actionItems || []).forEach((item: any) => { if (item.risks) risks.push(item.risks); });
  return getHighestRiskLevel(risks);
};

interface DashboardListProps {
  servers: ProjectInterface[];
  clickable?: boolean;
  emptyMessage?: string;
  renderRowActions?: (project: ProjectInterface) => React.ReactNode;
  linkTo?: (project: ProjectInterface) => string;
  /** When set, shows a sortable Risk header above the rows. */
  riskSort?: { direction: 'asc' | 'desc' | null; onToggle: () => void };
}

export const DashboardList = (
  { servers, clickable = true, emptyMessage = 'No projects found', renderRowActions, linkTo, riskSort }:
    DashboardListProps
) => (
  <div className="pt-20 px-6 bg-[#fafafa] min-h-screen">
    <div className="max-w-[1280px] mx-auto space-y-3">
      {riskSort && (
        <div className="flex justify-end px-4 text-xs font-medium text-[#444]">
          <button
            data-testid="projects-risk-sort"
            onClick={riskSort.onToggle}
            className="flex items-center gap-1 hover:text-[#171717] transition-colors"
            title="Sort by risk level"
          >
            Risk{riskSort.direction === 'asc' ? ' ↑' : riskSort.direction === 'desc' ? ' ↓' : ''}
          </button>
        </div>
      )}
      {servers.length === 0 ? (
        <div className="text-center py-16 text-[#888]">{emptyMessage}</div>
      ) : (
        servers.map((project) => {
          const defaultSite = project.sites?.find((s: any) => s.is_default) || project.sites?.[0];
          const location = getCityFromSite(defaultSite);
          const highestRisk = getProjectHighestRisk(project);
          const status = STATUS_CONFIG[project.status || 'NOT_STARTED'] || STATUS_CONFIG['NOT_STARTED'];
          const riskStripe = RISK_STRIPE[highestRisk] || RISK_STRIPE['NONE'];
          const riskColor = RISK_COLOR[highestRisk] || RISK_COLOR['NONE'];
          const itemCount = (project.items?.length || 0) + ((project as any).actionItems?.length || 0);
          const teamCount = project.users?.length || 0;

          const cardContent = (
            <div className="flex items-stretch">
                {/* Left risk stripe */}
                <div className={`w-1.5 flex-shrink-0 ${riskStripe}`} />

                {/* Content */}
                <div className="flex-1 flex items-center px-5 py-4 gap-8 min-w-0">
                  {/* Project name & description */}
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-[#171717] group-hover:text-[#000] transition-colors truncate">
                      {project.name}
                    </div>
                    {project.description && (
                      <p className="text-[12px] text-[#888] mt-0.5 truncate">{project.description}</p>
                    )}
                    {location && (
                      <div className="flex items-center gap-1 mt-1.5 text-[12px] text-[#aaa]">
                        <FaMapMarkerAlt className="w-3 h-3" />
                        <span>{location}</span>
                      </div>
                    )}
                  </div>

                  {/* Status badge */}
                  <div className="flex-shrink-0">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${status.bg} ${status.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                      {status.label}
                    </span>
                  </div>

                  {/* Risk indicator */}
                  <div className="flex-shrink-0 flex items-center gap-1.5 text-[12px] text-[#888] w-16">
                    <svg width="8" height="8" viewBox="0 0 8 8">
                      <circle cx="4" cy="4" r="4" fill={riskColor} />
                    </svg>
                    <span>{highestRisk.charAt(0) + highestRisk.slice(1).toLowerCase()}</span>
                  </div>

                  {/* Items count */}
                  <div className="flex-shrink-0 flex items-center gap-1.5 text-[12px] text-[#888] w-16">
                    <FaTasks className="w-3 h-3 text-[#aaa]" />
                    <span>{itemCount}</span>
                  </div>

                  {/* Team count */}
                  <div className="flex-shrink-0 flex items-center gap-1.5 text-[12px] text-[#888] w-16">
                    <FaUsers className="w-3 h-3 text-[#aaa]" />
                    <span>{teamCount}</span>
                  </div>

                  {/* Row actions */}
                  {renderRowActions && (
                    <div className="flex-shrink-0">
                      {renderRowActions(project)}
                    </div>
                  )}
                </div>
              </div>
          );

          return clickable ? (
            <Link
              key={project.id}
              to={linkTo ? linkTo(project) : `/projects/${project.id}`}
              className="block bg-white rounded-xl border border-[#eaeaea] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:border-[#ddd] transition-all group"
            >
              {cardContent}
            </Link>
          ) : (
            <div
              key={project.id}
              className="block bg-white rounded-xl border border-[#eaeaea] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
            >
              {cardContent}
            </div>
          );
        })
      )}
    </div>
  </div>
);
